'use es6';

import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { LanguageIcon, LocationIcon } from 'foundations-assets';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { CardSection } from 'foundations-components/transitional/components/Card';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { Link } from 'foundations-components/transitional/components/Link';
import { List } from 'foundations-components/transitional/components/List';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { View } from 'foundations-components/transitional/components/View';
import Rating from 'marketplace-ui-reviews/components/Rating';
import PartnerTypeIcon from 'marketplace-ui-solutions-core/components/PartnerTypeIcon';
import * as ProfileTierLabelKeys from 'marketplace-ui-solutions-core/constants/ProfileTierLabelKey';
import { OfficeLocation } from 'marketplace-ui-solutions-core/records/OfficeLocation';
import { ProfileListing } from 'marketplace-ui-solutions-core/records/ProfileListing';
import { getProfileTierLabelI18nKey } from 'marketplace-ui-solutions-core/utils/i18n';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { getGoogleMapsUrl } from '../../utils/urls';
import ProfileCardWrapper from './ProfileCardWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const I18N_PATH = 'ecosystem-marketplace-solutions-ui.profile.contact';
const ContactInfoCard = ({
  onTrackInteraction,
  profileListing,
  scrollToTabSection
}) => {
  const {
    locations,
    name,
    partnerType,
    reviewSummary,
    tier,
    websiteUrl
  } = profileListing;
  const trackOfficeLocationClick = useCallback(location => {
    onTrackInteraction('click view office location', {
      location
    });
  }, [onTrackInteraction]);
  const trackRemoteOfficeLocationClick = useCallback(() => {
    trackOfficeLocationClick('remote');
  }, [trackOfficeLocationClick]);
  const trackPhysicalOfficeLocationClick = useCallback(() => {
    trackOfficeLocationClick('physical');
  }, [trackOfficeLocationClick]);
  const renderedLocations = useMemo(() => {
    // All locations must be of the same type, either physical or remote
    const isRemote = locations.length && locations[0].get('remote');
    if (isRemote) {
      return /*#__PURE__*/_jsxs(TFlex, {
        justify: "center",
        children: [/*#__PURE__*/_jsx(View, {
          as: "span",
          marginInlineEnd: "100",
          children: /*#__PURE__*/_jsx(LocationIcon, {
            color: "do-not-use-eerie",
            size: "300"
          })
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${I18N_PATH}.remote`
        }), /*#__PURE__*/_jsx(HelpIcon, {
          "aria-label": unescapedText(`${I18N_PATH}.remoteTooltipAccessibleName`),
          marginInlineStart: "100",
          role: "tooltip",
          tooltipPlacement: "right",
          tooltipProps: {
            maxWidth: 250
          },
          title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: `${I18N_PATH}.remoteTooltip_jsx`,
            elements: {
              List
            },
            options: {
              profileName: name,
              locations: locations.map((loc, index) => {
                const officeLocation = new OfficeLocation(loc);
                return /*#__PURE__*/_jsx(Link, {
                  external: true,
                  href: getGoogleMapsUrl(officeLocation.buildFullAddress()),
                  onClick: trackRemoteOfficeLocationClick,
                  children: officeLocation.buildFullCity()
                }, index);
              })
            }
          })
        })]
      });
    }
    return locations.map((location, index) => {
      const officeLocation = new OfficeLocation(location);
      return /*#__PURE__*/_jsxs(TFlex, {
        justify: "center",
        children: [/*#__PURE__*/_jsx(View, {
          as: "span",
          marginInlineEnd: "100",
          children: /*#__PURE__*/_jsx(LocationIcon, {
            color: "do-not-use-eerie",
            size: "300"
          })
        }), /*#__PURE__*/_jsx(Link, {
          external: true,
          href: getGoogleMapsUrl(officeLocation.buildFullAddress()),
          onClick: trackPhysicalOfficeLocationClick,
          children: officeLocation.buildFullCity()
        })]
      }, index);
    });
  }, [locations, name, trackPhysicalOfficeLocationClick, trackRemoteOfficeLocationClick]);
  const formattedWebsiteUrl = useMemo(() => {
    if (websiteUrl && !/https?:\/\//i.test(websiteUrl)) {
      return `http://${websiteUrl}`;
    }
    return websiteUrl;
  }, [websiteUrl]);
  const renderedTierLabel = useMemo(() => {
    const profileTierLabelI18nKey = getProfileTierLabelI18nKey(tier, partnerType, ProfileTierLabelKeys.TITLE);
    if (!profileTierLabelI18nKey) {
      return null;
    }
    return /*#__PURE__*/_jsx(MediaObject, {
      align: "center",
      spacing: "extra-small",
      marginBlockEnd: "200",
      itemLeft: /*#__PURE__*/_jsx(PartnerTypeIcon, {
        bordered: true,
        partnerType: partnerType,
        tier: tier,
        size: 'small'
      }),
      children: /*#__PURE__*/_jsx(Text, {
        typography: "heading-25",
        textTransform: "uppercase",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: profileTierLabelI18nKey
        })
      })
    });
  }, [partnerType, tier]);
  return /*#__PURE__*/_jsx(ProfileCardWrapper, {
    children: /*#__PURE__*/_jsx(CardSection, {
      children: /*#__PURE__*/_jsxs(TFlex, {
        direction: "column",
        align: "center",
        children: [renderedTierLabel, /*#__PURE__*/_jsx(Text, {
          as: "h1",
          textAlign: "center",
          children: name
        }), /*#__PURE__*/_jsx(TFlex, {
          justify: "center",
          children: reviewSummary !== null && reviewSummary !== void 0 && reviewSummary.get('overallRating') ? /*#__PURE__*/_jsx(Rating, {
            rating: reviewSummary.get('overallRating'),
            justify: "center",
            label: /*#__PURE__*/_jsx(Link, {
              onClick: () => scrollToTabSection('reviews'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `ecosystem-marketplace-solutions-ui.profile.reviews.reviewCount`,
                options: {
                  count: reviewSummary.get('reviewCount')
                }
              })
            }),
            labelSize: "medium"
          }) : null
        }), /*#__PURE__*/_jsx(TFlex, {
          direction: "column",
          align: "center",
          justify: "center",
          marginBlockStart: "200",
          children: renderedLocations
        }), /*#__PURE__*/_jsxs(TFlex, {
          justify: "center",
          marginBlockStart: "200",
          children: [/*#__PURE__*/_jsx(View, {
            as: "span",
            marginInlineEnd: "100",
            children: /*#__PURE__*/_jsx(LanguageIcon, {
              color: "do-not-use-eerie",
              size: "300"
            })
          }), /*#__PURE__*/_jsx(Link, {
            external: true,
            href: formattedWebsiteUrl,
            onClick: () => onTrackInteraction('click visit agency website'),
            rel: "noopener",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `${I18N_PATH}.website`
            })
          })]
        }), /*#__PURE__*/_jsx(Button, {
          external: true,
          href: formattedWebsiteUrl,
          onClick: () => onTrackInteraction('click contact agency'),
          rel: "noopener",
          use: "tertiary",
          marginBlockStart: "600",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `${I18N_PATH}.business`
          })
        })]
      })
    })
  });
};
ContactInfoCard.propTypes = {
  onTrackInteraction: PropTypes.func.isRequired,
  profileListing: PropTypes.instanceOf(ProfileListing),
  scrollToTabSection: PropTypes.func.isRequired
};
export default ContactInfoCard;